import React, { useState, useEffect, useCallback } from 'react';
import Layout from '@/components/Layout';
import { API, useAuth } from '@/App';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, ArrowRight, Phone, Mail, Users, IndianRupee, MapPin, Loader2, TrendingUp, UserPlus } from 'lucide-react';
import { usePollenCapabilities } from '@/hooks/usePollenCapabilities';
import { useShowProfits } from '@/hooks/useShowProfits';

const PollenDotsTeamMemberProfile = () => {
  const POLLEN_API = `${API}/pollen`;
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const caps = usePollenCapabilities();
  const [showProfits] = useShowProfits();
  const profitVisible = caps.is_super_admin && showProfits;
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);

  const authFetch = (url, opts = {}) => {
    const h = {}; const t = localStorage.getItem('session_token'); if (t) h['Authorization'] = `Bearer ${t}`;
    return fetch(url, { ...opts, credentials: 'include', headers: { ...opts.headers, ...h } });
  };

  const fetchMember = useCallback(async () => {
    try {
      const res = await authFetch(`${POLLEN_API}/team/${id}`);
      if (res.ok) setMember(await res.json());
      else { toast.error('Team member not found'); navigate('/pollen-dots/team'); }
    } catch { toast.error('Failed to load'); }
    setLoading(false);
  }, [id]);

  useEffect(() => { fetchMember(); }, [fetchMember]);

  if (!['Pollen Dots User','Super Admin','Admin','Operator','Intern'].includes(user?.role)) return <Navigate to="/dashboard" replace />;
  if (loading) return <Layout><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></Layout>;
  if (!member) return <Layout><div className="text-center py-20 text-muted-foreground">Team member not found</div></Layout>;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="team-member-profile">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/pollen-dots/team')}><ArrowLeft className="w-4 h-4" /></Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold tracking-tight">{member.name}</h1>
            <div className="flex items-center gap-3 mt-1 flex-wrap text-sm text-muted-foreground">
              {member.role && <span>{member.role}</span>}
              {member.phone && <span><Phone className="w-3 h-3 inline mr-1" />{member.phone}</span>}
              {member.email && <span><Mail className="w-3 h-3 inline mr-1" />{member.email}</span>}
              {member.is_active ? <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Active</Badge>
                : <Badge variant="outline" className="bg-gray-100 text-gray-600">Inactive</Badge>}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="team-stats">
          <StatCard icon={Users} label="Referred Customers" value={member.referred_customers_count || 0} color="text-indigo-600" />
          <StatCard icon={MapPin} label="Total Tours" value={member.total_tours || 0} color="text-blue-600" />
          <StatCard icon={IndianRupee} label="Total Revenue" value={(member.total_revenue || 0).toLocaleString('en-IN')} color="text-amber-600" />
          {profitVisible && member.total_profit != null && (
            <StatCard icon={TrendingUp} label="Gross Profit" value={(member.total_profit || 0).toLocaleString('en-IN')} sub="Post FY 2026-27 only" color={(member.total_profit || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'} />
          )}
        </div>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><UserPlus className="w-4 h-4" />Customers Referred by {member.name}</CardTitle></CardHeader>
          <CardContent>
            {(member.referred_customers || []).length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No customers referred yet</p>
            ) : (
              <div className="space-y-2">
                {member.referred_customers.map((c) => (
                  <div key={c.id} className="p-3 border rounded-lg cursor-pointer hover:bg-muted/50 flex items-center justify-between"
                    onClick={() => navigate(`/pollen-dots/customer/${c.id}`)} data-testid={`tm-referred-${c.id}`}>
                    <div>
                      <p className="font-medium text-sm">{c.name}</p>
                      <div className="flex gap-3 text-xs text-muted-foreground">
                        {c.phone && <span><Phone className="w-3 h-3 inline mr-1" />{c.phone}</span>}
                        <span>{c.trip_count || 0} trip{(c.trip_count || 0) !== 1 ? 's' : ''}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-sm">{(c.lifetime_revenue || 0).toLocaleString('en-IN')}</p>
                      {profitVisible && c.gross_profit != null && <p className={`text-xs ${(c.gross_profit || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>Profit: {(c.gross_profit || 0).toLocaleString('en-IN')}</p>}
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground ml-2" />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

const StatCard = ({ icon: Icon, label, value, color, sub }) => (
  <Card><CardContent className="pt-4 pb-3 px-4">
    <div className="flex items-center gap-2">
      <div className={`p-1.5 rounded-lg bg-muted ${color}`}><Icon className="w-3.5 h-3.5" /></div>
      <div>
        <p className="text-[10px] text-muted-foreground leading-tight">{label}</p>
        <p className="text-lg font-bold">{value}</p>
        {sub && <p className="text-[9px] text-muted-foreground leading-tight">{sub}</p>}
      </div>
    </div>
  </CardContent></Card>
);

export default PollenDotsTeamMemberProfile;
